import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";

const KEY = "lk_spoiler_progress";
const SpoilerCtx = createContext(null);
const readLocal = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };

export const SpoilerProvider = ({ children }) => {
  const { user } = useAuth();
  const { series } = useSite();
  const [progress, setProgress] = useState(readLocal);

  useEffect(() => {
    if (!user) return;
    const remote = user.spoiler_progress || {};
    if (Object.keys(remote).length) { setProgress(remote); localStorage.setItem(KEY, JSON.stringify(remote)); }
    else if (Object.keys(readLocal()).length) api.put("/me/spoilers", { progress: readLocal() }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFinished = useCallback((seriesId, n) => {
    const next = { ...progress, [seriesId]: n };
    setProgress(next);
    localStorage.setItem(KEY, JSON.stringify(next));
    if (user) api.put("/me/spoilers", { progress: next }).catch(() => {});
  }, [progress, user]);

  const books = useMemo(() => {
    const byId = {}, byLabel = {};
    series.forEach((s) => (s.books || []).forEach((b) => {
      const info = { seriesId: s.id, number: b.number };
      byId[b.id] = info;
      byLabel[`Book ${b.number}: ${b.title}`] = info;
    }));
    return { byId, byLabel };
  }, [series]);

  const isUnlocked = useCallback((bookId, label) => {
    const b = books.byId[bookId] || books.byLabel[label];
    return !!b && (progress[b.seriesId] || 0) >= b.number;
  }, [books, progress]);

  return <SpoilerCtx.Provider value={{ progress, setFinished, isUnlocked }}>{children}</SpoilerCtx.Provider>;
};

export const useSpoilers = () => useContext(SpoilerCtx);
